import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BankIdStatus } from '@app/sales/models/sps-order-item.model';
import { VerificationType } from '@app/sales/modules/details/constants/Verification';
import {
  AddAuthenticatedBankIdSessionAction,
  CreateAuthenticatedVerificationErrorAction,
  SetCustomerAuthenticatedInMannyAction,
} from '@app/sales/modules/details/states/customer.actions';
import { CustomerSelectors } from '@app/sales/modules/details/states/customer.selectors';
import { VerificationService } from '@app/sales/services/verification.service';
import { UpdateCustomerValidationFormAction } from '@app/sales/states/form/form.actions';
import { select, Store } from '@ngxs/store';
import { filter, take } from 'rxjs/operators';

enum VerificationTypeQueryParam {
  BankId = 'BankId',
  TwoFA = 'TwoFactorAuthentication',
}

@Injectable({ providedIn: 'root' })
export class VerificationUrlListenerService {
  private readonly router = inject(Router);
  private readonly verificationService = inject(VerificationService);
  private readonly store = inject(Store);
  private readonly isAuthenticatedFromManny = select(CustomerSelectors.isAuthenticatedFromManny);

  constructor() {
    this.listenToVerificationUrls();
  }

  private listenToVerificationUrls() {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      const urlTree = this.router.parseUrl(this.router.url);

      const verificationType = urlTree.queryParams['verificationType'];
      const verificationCode = urlTree.queryParams['verificationCode'];

      if (verificationType === VerificationTypeQueryParam.BankId && verificationCode) {
        this.handleAuthenticatedBankIdSession(verificationCode);
      }

      if (verificationType === VerificationTypeQueryParam.TwoFA && verificationCode) {
        this.handleTwoFAVerification(verificationCode);
      }
    });
  }

  private handleAuthenticatedBankIdSession(code: string) {
    if (this.isAuthenticatedFromManny()) return;

    this.verificationService
      .createAuthenticatedVerification(code)
      .pipe(take(1))
      .subscribe({
        next: response => {
          if (response.verificationStatus === BankIdStatus.Match) {
            this.store.dispatch(new AddAuthenticatedBankIdSessionAction(response.id, response.verificationLinkCode));
            this.store.dispatch(new UpdateCustomerValidationFormAction({ type: VerificationType.bankID }));
          }
        },
        error: _error => {
          this.store.dispatch(new CreateAuthenticatedVerificationErrorAction());
        },
      });
  }

  private handleTwoFAVerification(code: string) {
    this.store.dispatch(new SetCustomerAuthenticatedInMannyAction());
    this.store.dispatch(new UpdateCustomerValidationFormAction({ type: VerificationType.TwoFA, verificationCode: code }));
  }
}
